import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  obtenerPrestamoPorId,
  actualizarPrestamo,
  getDataList
} from '../../api/common';
import Swal from 'sweetalert2';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Table,
  Button,
  Spinner,
  Badge,
  InputGroup,
  Alert
} from 'react-bootstrap';
import {
  FaSearch,
  FaEdit,
  FaSave,
  FaTimes,
  FaArrowLeft,
  FaImage,
  FaPlus,
  FaTrash,
  FaInfoCircle
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';

const EditarPrestamo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Estados
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [loadingEquipos, setLoadingEquipos] = useState(false);
  const [prestamo, setPrestamo] = useState(null);
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [equiposSeleccionados, setEquiposSeleccionados] = useState([]);
  const [ots, setOts] = useState([]);
  const [mostrarAgregarEquipos, setMostrarAgregarEquipos] = useState(false);

  // Filtros de tabla de equipos disponibles
  const [filtros, setFiltros] = useState({
    inventario: '',
    item: '',
    serie: '',
    marca: '',
    ubicacion: ''
  });

  // Datos del formulario
  const [formData, setFormData] = useState({
    ot: '',
    observaciones: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    try {
      setLoading(true);

      // Cargar préstamo
      const respPrestamo = await obtenerPrestamoPorId(id);

      if (respPrestamo.status === 200) {
        const prestamoData = respPrestamo.data.data;
        setPrestamo(prestamoData);

        // Establecer datos del formulario
        setFormData({
          ot: prestamoData.ot?._id || prestamoData.ot,
          observaciones: prestamoData.observaciones || ''
        });

        // Equipos ya incluidos en el préstamo
        setEquiposSeleccionados(
          prestamoData.equipos.map(eq => eq.patron._id || eq.patron)
        );
      } else {
        Swal.fire('Error', 'No se pudo cargar el préstamo', 'error');
        navigate('/prestamos');
      }

      // Cargar OTs activas
      const respOts = await getDataList('Ot', {
        conditions: {
          otType: 'Parent',
          estado: { $in: ['Solicitud', 'Programada', 'Abierta'] }
        }
      });

      if (respOts.status === 200) {
        setOts(respOts.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      Swal.fire('Error', 'Ocurrió un error al cargar los datos', 'error');
      navigate('/prestamos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar equipos disponibles para agregar
  const cargarEquiposDisponibles = async () => {
    try {
      setLoadingEquipos(true);

      const respEquipos = await getDataList('Patrones',{conditions: {
        tipoEquipo: 'Prestamo',
        estado: 'Activo'
      }});

      if (respEquipos.status === 200) {
        // Filtrar equipos que ya están en el préstamo
        const equiposYaIncluidos = prestamo.equipos.map(
          eq => eq.patron._id || eq.patron
        );
        const equiposFiltrados = (respEquipos.data.data || []).filter(
          eq => !equiposYaIncluidos.includes(eq._id)
        );
        setEquiposDisponibles(equiposFiltrados);
      }
    } catch (error) {
      console.error('Error al cargar equipos:', error);
      Swal.fire('Error', 'No se pudieron cargar los equipos disponibles', 'error');
    } finally {
      setLoadingEquipos(false);
    }
  };

  // Filtrar equipos disponibles según los criterios (y excluir ya seleccionados)
  const equiposFiltrados = useMemo(() => {
    return equiposDisponibles.filter(equipo => {
      // No mostrar equipos ya seleccionados
      if (equiposSeleccionados.includes(equipo._id)) {
        return false;
      }
      
      const cumpleInventario =
        !filtros.inventario ||
        equipo.inventario?.toLowerCase().includes(filtros.inventario.toLowerCase());

      const cumpleItem =
        !filtros.item ||
        equipo.item?.toLowerCase().includes(filtros.item.toLowerCase());

      const cumpleSerie =
        !filtros.serie ||
        equipo.serie?.toLowerCase().includes(filtros.serie.toLowerCase());

      const cumpleMarca =
        !filtros.marca ||
        equipo.marca?.toLowerCase().includes(filtros.marca.toLowerCase());

      const cumpleUbicacion =
        !filtros.ubicacion ||
        equipo.ubicacion?.toLowerCase().includes(filtros.ubicacion.toLowerCase());

      return cumpleInventario && cumpleItem && cumpleSerie && cumpleMarca && cumpleUbicacion;
    });
  }, [equiposDisponibles, filtros, equiposSeleccionados]);

  // Manejar cambio en filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      inventario: '',
      item: '',
      serie: '',
      marca: '',
      ubicacion: ''
    });
  };

  // Manejar cambio en formulario
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Agregar equipo al préstamo
  const handleAgregarEquipo = equipoId => {
    if (!equiposSeleccionados.includes(equipoId)) {
      setEquiposSeleccionados([...equiposSeleccionados, equipoId]);
      
      // Agregar equipo al array de equipos del préstamo para mostrarlo en la tabla
      const equipoData = equiposDisponibles.find(eq => eq._id === equipoId);
      if (equipoData) {
        setPrestamo(prev => ({
          ...prev,
          equipos: [
            ...prev.equipos,
            {
              patron: {
                _id: equipoData._id,
                inventario: equipoData.inventario,
                item: equipoData.item,
                serie: equipoData.serie,
                marca: equipoData.marca,
                modelo: equipoData.modelo,
                foto: equipoData.foto
              },
              inventario: equipoData.inventario,
              item: equipoData.item,
              descripcion: equipoData.item,
              serie: equipoData.serie,
              marca: equipoData.marca,
              modelo: equipoData.modelo,
              foto: equipoData.foto,
              estado: 'prestado'
            }
          ]
        }));
        
        // Remover de equipos disponibles
        setEquiposDisponibles(prev => prev.filter(eq => eq._id !== equipoId));
      }
    }
  };

  // Remover equipo del préstamo (solo equipos aún no confirmados)
  const handleRemoverEquipo = equipoId => {
    // Verificar si el equipo ya fue confirmado
    const equipoEnPrestamo = prestamo.equipos.find(
      eq => (eq.patron._id || eq.patron) === equipoId
    );

    if (equipoEnPrestamo && equipoEnPrestamo.estado !== 'prestado') {
      Swal.fire(
        'No permitido',
        'No se puede remover un equipo que ya fue devuelto o está en proceso de devolución',
        'warning'
      );
      return;
    }

    // Remover de seleccionados
    setEquiposSeleccionados(equiposSeleccionados.filter(id => id !== equipoId));
    
    // Si el equipo es nuevo (agregado en esta sesión), devolverlo a disponibles
    if (equipoEnPrestamo) {
      const equipoParaDevolver = {
        _id: equipoEnPrestamo.patron._id || equipoEnPrestamo.patron,
        inventario: equipoEnPrestamo.inventario || equipoEnPrestamo.patron?.inventario,
        item: equipoEnPrestamo.item || equipoEnPrestamo.patron?.item,
        serie: equipoEnPrestamo.serie || equipoEnPrestamo.patron?.serie,
        marca: equipoEnPrestamo.marca || equipoEnPrestamo.patron?.marca,
        modelo: equipoEnPrestamo.modelo || equipoEnPrestamo.patron?.modelo,
        foto: equipoEnPrestamo.foto || equipoEnPrestamo.patron?.foto,
        ubicacion: equipoEnPrestamo.ubicacionAnterior || 'Disponible'
      };
      
      setEquiposDisponibles(prev => [...prev, equipoParaDevolver]);
    }
    
    // Remover del prestamo local
    setPrestamo(prev => ({
      ...prev,
      equipos: prev.equipos.filter(eq => (eq.patron._id || eq.patron) !== equipoId)
    }));
  };

  // Verificar si un equipo está seleccionado
  const estaSeleccionado = equipoId => {
    return equiposSeleccionados.includes(equipoId);
  };

  // Validar formulario
  const validarFormulario = () => {
    if (!formData.ot) {
      Swal.fire('Validación', 'Debe seleccionar una OT', 'warning');
      return false;
    }

    if (equiposSeleccionados.length === 0) {
      Swal.fire('Validación', 'Debe tener al menos un equipo en el préstamo', 'warning');
      return false;
    }

    return true;
  };

  // Guardar cambios
  const handleGuardarCambios = async () => {
    if (!validarFormulario()) return;

    try {
      setGuardando(true);

      // Preparar equipos actualizados
      const equiposActualizados = await Promise.all(
        equiposSeleccionados.map(async equipoId => {
          // Verificar si el equipo ya estaba en el préstamo
          const equipoExistente = prestamo.equipos.find(
            eq => (eq.patron._id || eq.patron) === equipoId
          );

          if (equipoExistente) {
            // Mantener el equipo existente con su estado actual
            return equipoExistente;
          } else {
            // Nuevo equipo agregado
            const equipoData = equiposDisponibles.find(eq => eq._id === equipoId);
            return {
              patron: equipoId,
              inventario: equipoData.inventario,
              item: equipoData.item,
              descripcion: equipoData.item,
              serie: equipoData.serie,
              marca: equipoData.marca,
              modelo: equipoData.modelo,
              foto: equipoData.foto,
              ubicacionAnterior: equipoData.ubicacion || equipoData.movimiento,
              estado: 'prestado'
            };
          }
        })
      );

      const datosActualizados = {
        ot: formData.ot,
        equipos: equiposActualizados,
        observaciones: formData.observaciones
      };

      const resp = await actualizarPrestamo(id, datosActualizados);

      if (resp.status === 200) {
        Swal.fire({
          icon: 'success',
          title: '¡Préstamo actualizado!',
          text: 'Los cambios se guardaron correctamente',
          confirmButtonText: 'Aceptar'
        }).then(() => {
          navigate('/prestamos');
        });
      } else {
        Swal.fire('Error', resp.message || 'No se pudo actualizar el préstamo', 'error');
      }
    } catch (error) {
      console.error('Error al actualizar préstamo:', error);
      Swal.fire('Error', 'Ocurrió un error al actualizar el préstamo', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Mostrar sección de agregar equipos
  const handleMostrarAgregarEquipos = () => {
    cargarEquiposDisponibles();
    setMostrarAgregarEquipos(true);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando información del préstamo...</p>
      </Container>
    );
  }

  if (!prestamo) {
    return (
      <Container className="py-5">
        <Alert variant="danger">No se pudo cargar la información del préstamo</Alert>
      </Container>
    );
  }

  // Verificar si el préstamo puede ser editado
  const puedeEditar = ['activo', 'confirmado'].includes(prestamo.estado);

  if (!puedeEditar) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <FaInfoCircle className="me-2" />
          Este préstamo no puede ser editado porque su estado es: <strong>{prestamo.estado}</strong>
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/prestamos')}>
          <FaArrowLeft className="me-2" />
          Volver a préstamos
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate('/prestamos')}
            className="mb-2"
          >
            <FaArrowLeft className="me-2" />
            Volver
          </Button>
          <h2 className="mb-0">
            <FaEdit className="me-2 text-primary" />
            Editar Préstamo
          </h2>
          <p className="text-muted">
            Acta: <strong>{prestamo.numeroActa}</strong> | Estado:{' '}
            <Badge bg="info">{prestamo.estado}</Badge>
          </p>
        </Col>
      </Row>

      <Row>
        {/* Formulario Principal */}
        <Col lg={4}>
          <Card className="shadow-sm mb-4 w-100">
            <Card.Header className="bg-primary text-white w-100">
              <h5 className="mb-0">Información del Préstamo</h5>
            </Card.Header>
            <Card.Body className="w-100">
              <Form>
                {/* OT */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Orden de Trabajo <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="ot"
                    value={formData.ot}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccione OT...</option>
                    {ots.map(ot => (
                      <option key={ot._id} value={ot._id}>
                        {ot.numeroOrden} - {ot.cliente?.nombre || 'Sin cliente'}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Usuario creador */}
                <Form.Group className="mb-3">
                  <Form.Label>Creado por</Form.Label>
                  <Form.Control
                    type="text"
                    value={prestamo.nombreUsuarioCreacion || ''}
                    disabled
                  />
                </Form.Group>

                {/* Fecha de creación */}
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de creación</Form.Label>
                  <Form.Control
                    type="text"
                    value={new Date(prestamo.fechaCreacion).toLocaleDateString()}
                    disabled
                  />
                </Form.Group>

                {/* Observaciones */}
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="observaciones"
                    value={formData.observaciones}
                    onChange={handleInputChange}
                    placeholder="Observaciones adicionales..."
                  />
                </Form.Group>

                {/* Estado actual */}
                <div className="mb-3">
                  <strong>Estado actual:</strong>
                  <Badge bg="info" className="ms-2">
                    {prestamo.estado}
                  </Badge>
                </div>

                {/* Confirmación de entrega */}
                {prestamo.confirmacionEntrega?.confirmado && (
                  <Alert variant="success" className="mb-3">
                    <small>
                      <FaInfoCircle className="me-2" />
                      Entrega confirmada por {prestamo.confirmacionEntrega.nombreTecnico}
                      <br />
                      {new Date(prestamo.confirmacionEntrega.fecha).toLocaleString()}
                    </small>
                  </Alert>
                )}

                {/* Botones */}
                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleGuardarCambios}
                    disabled={guardando}
                  >
                    {guardando ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Guardando cambios...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Guardar Cambios
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/prestamos')}
                    disabled={guardando}
                  >
                    <FaTimes className="me-2" />
                    Cancelar
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Equipos del Préstamo */}
        <Col lg={8}>
          <Card className="shadow-sm mb-3 w-100">
            <Card.Header className="bg-white w-100">
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    Equipos en el Préstamo{' '}
                    <Badge bg="info">{equiposSeleccionados.length}</Badge>
                  </h5>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleMostrarAgregarEquipos}
                  >
                    <FaPlus className="me-2" />
                    Agregar Equipos
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0 w-100">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped hover className="mb-0">
                  <thead className="bg-light sticky-top">
                    <tr>
                      <th style={{ width: '80px' }}>Foto</th>
                      <th>Inventario</th>
                      <th>Descripción</th>
                      <th>Serie</th>
                      <th>Marca</th>
                      <th>Estado</th>
                      <th style={{ width: '80px' }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prestamo.equipos
                      .filter(eq =>
                        equiposSeleccionados.includes(eq.patron._id || eq.patron)
                      )
                      .map((eq, index) => (
                        <tr key={index}>
                          <td>
                            {eq.foto || eq.patron?.foto ? (
                              <img
                                src={eq.foto || eq.patron.foto}
                                alt={eq.item}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  borderRadius: '4px'
                                }}
                                onError={e => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <FaImage style={{ fontSize: '30px', color: '#ccc' }} />
                            )}
                          </td>
                          <td>
                            <strong>{eq.inventario || eq.patron?.inventario}</strong>
                          </td>
                          <td>{eq.item || eq.descripcion || eq.patron?.item}</td>
                          <td>{eq.serie || eq.patron?.serie}</td>
                          <td>{eq.marca || eq.patron?.marca}</td>
                          <td>
                            {eq.estado === 'devuelto' ? (
                              <Badge bg="success">Devuelto</Badge>
                            ) : (
                              <Badge bg="warning">Prestado</Badge>
                            )}
                          </td>
                          <td>
                            {eq.estado === 'prestado' && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleRemoverEquipo(eq.patron._id || eq.patron)
                                }
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    {equiposSeleccionados.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <p className="text-muted mb-0">
                            No hay equipos en este préstamo
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Sección Agregar Equipos */}
          {mostrarAgregarEquipos && (
            <Card className="shadow-sm w-100">
              <Card.Header className="bg-success text-white w-100">
                <Row className="align-items-center">
                  <Col>
                    <h5 className="mb-0">Agregar Equipos Disponibles</h5>
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={limpiarFiltros}
                    >
                      Limpiar Filtros
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="ms-2"
                      onClick={() => setMostrarAgregarEquipos(false)}
                    >
                      <FaTimes />
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0 w-100">
                {loadingEquipos ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="success" />
                    <p className="mt-2">Cargando equipos disponibles...</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Table striped hover responsive className="mb-0">
                      <thead className="bg-light sticky-top">
                        <tr>
                          <th style={{ width: '80px' }}>Foto</th>
                          <th>
                            <div className="mb-2">Inventario</div>
                            <InputGroup size="sm">
                              <InputGroup.Text>
                                <FaSearch />
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                placeholder="Filtrar..."
                                value={filtros.inventario}
                                onChange={e =>
                                  handleFiltroChange('inventario', e.target.value)
                                }
                              />
                            </InputGroup>
                          </th>
                          <th>
                            <div className="mb-2">Descripción/Item</div>
                            <InputGroup size="sm">
                              <InputGroup.Text>
                                <FaSearch />
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                placeholder="Filtrar..."
                                value={filtros.item}
                                onChange={e => handleFiltroChange('item', e.target.value)}
                              />
                            </InputGroup>
                          </th>
                          <th>
                            <div className="mb-2">Serie</div>
                            <InputGroup size="sm">
                              <InputGroup.Text>
                                <FaSearch />
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                placeholder="Filtrar..."
                                value={filtros.serie}
                                onChange={e => handleFiltroChange('serie', e.target.value)}
                              />
                            </InputGroup>
                          </th>
                          <th>
                            <div className="mb-2">Marca</div>
                            <InputGroup size="sm">
                              <InputGroup.Text>
                                <FaSearch />
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                placeholder="Filtrar..."
                                value={filtros.marca}
                                onChange={e => handleFiltroChange('marca', e.target.value)}
                              />
                            </InputGroup>
                          </th>
                          <th style={{ width: '100px' }}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {equiposFiltrados.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <p className="text-muted mb-0">
                                No hay equipos disponibles con los filtros aplicados
                              </p>
                            </td>
                          </tr>
                        ) : (
                          equiposFiltrados.map(equipo => (
                            <tr key={equipo._id}>
                              <td>
                                {equipo.foto ? (
                                  <img
                                    src={equipo.foto}
                                    alt={equipo.item}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      objectFit: 'cover',
                                      borderRadius: '4px'
                                    }}
                                    onError={e => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <FaImage style={{ fontSize: '30px', color: '#ccc' }} />
                                )}
                              </td>
                              <td>
                                <strong>{equipo.inventario}</strong>
                              </td>
                              <td>{equipo.item}</td>
                              <td>{equipo.serie}</td>
                              <td>{equipo.marca}</td>
                              <td>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleAgregarEquipo(equipo._id)}
                                >
                                  <FaPlus />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default EditarPrestamo;